import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SearchX, MessageCircle } from 'lucide-react';
import { searchProductsDetailed, STORE } from '../data/catalog';
import ProductCard from '../components/ProductCard';
import { isProductVisible, useAvailabilityTick } from '../utils/availability';

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get('q') || '';
  useAvailabilityTick();
  const results = useMemo(
    () => searchProductsDetailed(q).filter((r) => isProductVisible(r.product)),
    [q]
  );

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 md:py-12" data-testid="search-page">
      <h1 className="font-serif text-3xl tracking-tight text-navy md:text-4xl">
        Resultados para “{q}”
      </h1>
      <p className="mt-2 text-sm text-muted-foreground" data-testid="search-result-count">
        {results.length} {results.length === 1 ? 'tecido encontrado' : 'tecidos encontrados'}
      </p>

      {results.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-navy/20 bg-white p-10 text-center md:p-16" data-testid="search-empty-state">
          <SearchX className="mx-auto h-10 w-10 text-navy/40" strokeWidth={1.5} />
          <p className="mt-4 font-serif text-2xl text-navy">Nenhum tecido encontrado</p>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
            Não encontramos resultados para “{q}”. Tente outra palavra, navegue pelas categorias ou
            fale com nossa equipe — podemos ter o tecido na loja física.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/categoria/todos"
              className="rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
              data-testid="search-empty-browse-button"
            >
              EXPLORAR TECIDOS
            </Link>
            <a
              href={`https://wa.me/${STORE.whatsappDigits}?text=${encodeURIComponent(`Olá! Estou procurando "${q}" no site de vocês.`)}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-full border border-[#128C4B]/40 px-7 py-3 text-sm font-semibold text-[#128C4B] hover:bg-green-50"
              data-testid="search-empty-whatsapp-button"
            >
              <MessageCircle className="h-4 w-4" /> PERGUNTAR NO WHATSAPP
            </a>
          </div>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4" data-testid="search-results-grid">
          {results.map(({ product, variantMatch }, i) => (
            <ProductCard
              key={product.slug}
              product={product}
              index={i}
              variantMatch={variantMatch.length ? variantMatch.map((v) => v.name).join(' · ') : null}
            />
          ))}
        </div>
      )}
    </main>
  );
}
